import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, User, Flame, UserCog } from "lucide-react";
import ContentTagPicker from "@/components/ContentTagPicker";
import { AthleteSelect } from "@/components/AthleteSelect";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";

const DURATIONS = [10, 12, 15, 20, 25, 30];
const INTENSITIES = [1, 2, 3, 4, 5];

/**
 * QuickLessonDialog — registrazione rapida lezione (target ≤ 15s).
 * Il tecnico esecutore è pre-selezionato ma modificabile via chip.
 */
export default function QuickLessonDialog({ open, onOpenChange, sessionId, defaultCoachId, weapon = null, onSaved }) {
  const [athletes, setAthletes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [athleteQ, setAthleteQ] = useState("");
  const [athleteId, setAthleteId] = useState("");
  const [leadCoachId, setLeadCoachId] = useState("");
  const [duration, setDuration] = useState(15);
  const [intensity, setIntensity] = useState(null);
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/athletes"), api.get("/coaches")]).then(([a, u]) => {
      setAthletes(a.data || []);
      setCoaches(u.data || []);
    }).catch(() => {});
    setAthleteId(""); setAthleteQ(""); setTags([]); setIntensity(null); setDuration(15);
    setLeadCoachId(defaultCoachId || "");
  }, [open, defaultCoachId]);

  const filteredAthletes = useMemo(() => {
    const needle = athleteQ.trim().toLowerCase();
    let list = athletes.filter((a) => a.active !== false && !a.archived);
    if (needle) {
      list = list.filter((a) => matchesSearch(a, needle));
    }
    return list.slice(0, 30);
  }, [athletes, athleteQ]);

  const chosen = useMemo(() => athletes.find((a) => a.id === athleteId), [athletes, athleteId]);

  const save = useCallback(async () => {
    if (!athleteId) { toast.error("Seleziona uno schermitore"); return; }
    if (!leadCoachId) { toast.error("Seleziona il tecnico esecutore"); return; }
    setSaving(true);
    try {
      let sid = sessionId;
      if (!sid) {
        const { data } = await api.post("/training/ensure-session");
        sid = data.id;
      }
      const payload = {
        session_id: sid, type: "individual_lesson",
        lead_coach_id: leadCoachId, athlete_ids: [athleteId],
        duration_min: duration, content_tag_codes: tags,
      };
      if (intensity != null) payload.intensity = intensity;
      const { data: activity } = await api.post("/activities", payload);
      try { await api.post(`/activities/${activity.id}/complete`); } catch {}
      toast.success(`Lezione salvata · ${duration}′`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio lezione");
    } finally { setSaving(false); }
  }, [athleteId, leadCoachId, sessionId, duration, intensity, tags, onOpenChange, onSaved]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-lesson-dialog" className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><User className="w-5 h-5" />Nuova lezione</DialogTitle>
          <DialogDescription>Registra una lezione individuale in pochi secondi.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="flex items-center gap-1"><UserCog className="w-3.5 h-3.5" />Tecnico esecutore *</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {coaches.map((c) => (
                <button
                  key={c.id}
                  data-testid={`ql-lead-${c.id}`}
                  onClick={() => setLeadCoachId(c.id)}
                  className={`h-10 px-3 rounded-full text-sm border ${
                    leadCoachId === c.id ? "bg-black text-white border-black" : "bg-white border-zinc-300"
                  }`}
                >{c.name}</button>
              ))}
            </div>
          </div>

          {!chosen ? (
            <div>
              <Label>Schermitore *</Label>
              <div className="mt-1">
                <AthleteSelect
                  value={athleteId}
                  onChange={setAthleteId}
                  athletes={athletes}
                  testid="ql-athlete"
                  placeholder="Cerca e seleziona schermitore"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-md px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-semibold">
                {initials(chosen)}
              </div>
              <div className="flex-1">
                <div className="font-semibold">{displayName(chosen)}</div>
                <div className="text-xs text-zinc-500">{chosen.weapon || "—"} · {chosen.category || "—"}</div>
              </div>
              <button className="text-xs text-zinc-500 hover:text-black underline" data-testid="ql-change-athlete" onClick={() => setAthleteId("")}>
                Cambia
              </button>
            </div>
          )}

          <div>
            <Label>Durata (min)</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  data-testid={`ql-dur-${d}`}
                  onClick={() => setDuration(d)}
                  className={`h-11 px-4 rounded-md border text-sm ${
                    duration === d ? "bg-black text-white border-black" : "bg-white text-zinc-800 border-zinc-300"
                  }`}
                >{d}′</button>
              ))}
            </div>
          </div>

          <div>
            <Label>Contenuti</Label>
            <ContentTagPicker value={tags} onChange={setTags} weapon={weapon} maxHeight="180px" />
          </div>

          <div>
            <Label className="flex items-center gap-1"><Flame className="w-3.5 h-3.5" />Intensità (facoltativa)</Label>
            <div className="flex gap-2 mt-1">
              {INTENSITIES.map((i) => (
                <button
                  key={i}
                  data-testid={`ql-int-${i}`}
                  onClick={() => setIntensity(intensity === i ? null : i)}
                  className={`h-11 w-11 rounded-md border text-sm ${
                    intensity === i ? "bg-black text-white border-black" : "bg-white border-zinc-300"
                  }`}
                >{i}</button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            data-testid="ql-save"
            onClick={save}
            disabled={saving || !athleteId || !leadCoachId}
            className="bg-black hover:bg-zinc-800 h-11 px-6"
          >
            {saving ? "Salvo…" : "Salva lezione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
